import type {
  Channel,
  ChannelData,
  ChannelFilters,
  ChannelOptions,
  ChannelResponse,
  ChannelSort,
  QueryChannelAPIResponse,
} from "stream-chat";
import { StreamChat } from "stream-chat";
import { env } from "~/config/env";

// Create a proper mock response type
interface MockChannelResponse extends ChannelResponse {
  disabled: boolean;
  frozen: boolean;
  id: string;
  type: string;
  cid: string;
}

interface MockQueryChannelResponse extends QueryChannelAPIResponse {
  channel: MockChannelResponse;
  duration: string;
  members: any[]; // Changed from Record<string, any> to any[]
  messages: any[];
  pinned_messages: any[];
}

const streamChatClient = StreamChat.getInstance(env.VITE_STREAM_CHAT_API_KEY, {
  /* Set the client server location to EU West region for speed */
  baseURL: "https://chat-proxy-dublin.stream-io-api.com",

  enableInsights: false,

  enableWSFallback: false,
  /* Basic timeout is 3s, which can be too fast for low connexions */
  timeout: 30000,
});

if (typeof window !== "undefined") {
  // Store original methods
  const originalSetUser = streamChatClient.setUser.bind(streamChatClient);
  const originalQueryChannels =
    streamChatClient.queryChannels.bind(streamChatClient);
  const originalChannel = streamChatClient.channel.bind(streamChatClient);

  // Override setUser for offline mode
  streamChatClient.setUser = async (user, tokenOrPromise) => {
    if (!navigator.onLine) {
      return;
    }
    return originalSetUser(user, tokenOrPromise);
  };

  // Override queryChannels for offline mode
  streamChatClient.queryChannels = async (
    filters: ChannelFilters,
    sort?: ChannelSort,
    options?: ChannelOptions
  ) => {
    if (!navigator.onLine) {
      return [];
    }
    return originalQueryChannels(filters, sort, options);
  };

  // Override channel method for offline mode
  streamChatClient.channel = function (
    type: string,
    idOrData?: ChannelData | string | null,
    data?: ChannelData
  ): Channel {
    // Handle both function signatures
    const id =
      typeof idOrData === "string" || idOrData === null ? idOrData : undefined;
    const channelData =
      typeof idOrData === "object" && idOrData !== null ? idOrData : data;

    const channel = originalChannel(type, id, channelData);

    if (!navigator.onLine) {
      // Override watch method when offline
      const originalWatch = channel.watch.bind(channel);
      channel.watch = async (options?): Promise<MockQueryChannelResponse> => {
        console.log("[OFFLINE] Skipping channel.watch - offline mode");
        return {
          channel: {
            cid: channel.cid,
            disabled: false,
            frozen: false,
            id: channel.id ?? "",
            type: channel.type,
          } as MockChannelResponse,
          duration: "0ms",
          members: [],
          messages: [],
          pinned_messages: [],
        };
      };
    }

    return channel;
  };
}

type StreamChatClient = typeof streamChatClient;

export type { StreamChatClient };
export { streamChatClient };
